/**
 * Marca oficial da UCC.
 *
 * Duas peças, porque a arte oficial é uma assinatura completa — emblema em
 * cima, três linhas de texto embaixo:
 *
 *   emblema     só o brasão. É o que vai em cabeçalho, rodapé e favicon.
 *   assinatura  a arte inteira, para onde há espaço de sobra.
 *
 * Usar a assinatura nos lugares pequenos era o defeito: aos 30px as três
 * linhas de texto viram borrão, e ao lado de "Ultimate Clubs Championship" o
 * nome aparecia duas vezes. Quem lê pequeno precisa do símbolo, não do nome
 * repetido.
 *
 * A altura é que manda, e a largura acompanha a proporção — o emblema é mais
 * largo que alto, e travá-lo num quadrado o encolheria para caber.
 *
 * Os arquivos são servidos de `public/` em vez de importados pelo bundler:
 * assim o navegador os busca em paralelo com o JavaScript, e a marca aparece
 * sem esperar o pacote da aplicação. São os mesmos que alimentam o favicon.
 */

import { cn } from '@/lib/cn';

/** Proporções reais dos arquivos, para reservar o espaço antes de carregarem. */
const PROPORCAO = {
  emblema: 1125 / 778,
  assinatura: 1125 / 1198,
} as const;

interface Props {
  /** Altura em pixels. A largura sai da proporção da arte. */
  altura?: number;
  /** `assinatura` inclui o texto da arte — só onde couber grande. */
  variante?: 'emblema' | 'assinatura';
  className?: string;
}

export function LogoUCC({ altura = 32, variante = 'emblema', className }: Props) {
  const proporcao = PROPORCAO[variante];
  const largura = Math.round(altura * proporcao);

  return (
    <img
      src={variante === 'emblema' ? '/logo.webp' : '/logo-completa.webp'}
      width={largura}
      height={altura}
      alt=""
      aria-hidden
      draggable={false}
      className={cn('shrink-0 object-contain select-none', className)}
      style={{ height: altura, width: largura }}
    />
  );
}
